import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/apiClient";
import { LockedAutocomplete } from "@/components/LockedAutocomplete";
import { pitchSource, pitchToOption } from "@/lib/autocompleteSources";
import { formatApiError } from "@/lib/formatApiError";
import {
  applyFernausleseChannelImport,
  getFernausleseChannels,
  previewFernausleseChannelImport,
  type FernausleseChannel,
  type FernausleseChannelMappingSuggestion,
} from "@/lib/fernausleseApi";
import { sortPitchesByTouristPlanr } from "@/lib/touristImportMatching";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import type { Pitch } from "@/types/camping";

type MappingRow = FernausleseChannelMappingSuggestion & { selected_pitch_id: string };

interface FernausleseChannelMappingsSectionProps {
  canWrite: boolean;
  sectionId?: string;
}

export function FernausleseChannelMappingsSection({
  canWrite,
  sectionId = "fernauslese-kanaele",
}: FernausleseChannelMappingsSectionProps) {
  const [pitches, setPitches] = useState<Pitch[]>([]);
  const [savedChannels, setSavedChannels] = useState<FernausleseChannel[]>([]);
  const [savedMappedCount, setSavedMappedCount] = useState(0);
  const [loadingSaved, setLoadingSaved] = useState(true);

  const [csvContent, setCsvContent] = useState("");
  const [rows, setRows] = useState<MappingRow[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [previewed, setPreviewed] = useState(false);

  const loadSavedChannels = useCallback(() => {
    setLoadingSaved(true);
    getFernausleseChannels()
      .then((result) => {
        setSavedChannels(result.channels);
        setSavedMappedCount(result.mapped_count);
      })
      .catch(() => {
        setSavedChannels([]);
        setSavedMappedCount(0);
      })
      .finally(() => setLoadingSaved(false));
  }, []);

  useEffect(() => {
    api
      .get<Pitch[]>("/api/v1/pitches?include_inactive=true")
      .then(setPitches)
      .catch(() => setPitches([]));
    loadSavedChannels();
  }, [loadSavedChannels]);

  const onFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setCsvContent(String(reader.result ?? ""));
      setPreviewed(false);
      setRows([]);
      setWarnings([]);
      setSuccess(null);
    };
    reader.readAsText(file, "utf-8");
    e.target.value = "";
  }, []);

  async function onPreview() {
    if (!csvContent.trim()) {
      setError("Bitte eine CSV-Datei hochladen oder Inhalt einfügen.");
      return;
    }
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const preview = await previewFernausleseChannelImport(csvContent);
      setRows(
        preview.channels.map((ch) => ({
          ...ch,
          selected_pitch_id: ch.pitch_id ?? "",
        })),
      );
      setWarnings(preview.duplicate_pitch_warnings);
      setPreviewed(true);
    } catch (err) {
      setError(formatApiError(err, "Vorschau fehlgeschlagen"));
    } finally {
      setLoading(false);
    }
  }

  async function onApply() {
    if (!canWrite || rows.length === 0) return;
    setApplying(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await applyFernausleseChannelImport(
        rows.map((row) => ({
          channel_id: row.channel_id,
          pitch_id: row.selected_pitch_id || null,
        })),
        csvContent,
      );
      setSuccess(`${result.updated} Zuordnung(en) gespeichert.`);
      setPreviewed(false);
      setRows([]);
      setCsvContent("");
      loadSavedChannels();
    } catch (err) {
      setError(formatApiError(err, "Zuordnung konnte nicht gespeichert werden"));
    } finally {
      setApplying(false);
    }
  }

  const mappedCount = useMemo(
    () => rows.filter((row) => row.selected_pitch_id).length,
    [rows],
  );

  function pitchesForRow(description: string): Pitch[] {
    return sortPitchesByTouristPlanr(pitches, description);
  }

  return (
    <div id={sectionId} className="space-y-4 scroll-mt-6">
      <Card
        title="Importierte Kanäle"
        bodyClassName="space-y-4"
        actions={
          savedChannels.length > 0 ? (
            <span className="text-xs text-label-secondary">
              {savedMappedCount} von {savedChannels.length} zugeordnet
            </span>
          ) : undefined
        }
      >
        <p className="text-sm text-label-secondary">
          Kanäle aus zfa.io und ihre Zuordnung zu CampDesk-Stellplätzen. Die Zuordnung wird bei
          Check-in und Check-out für die Stromsteuerung verwendet.
        </p>
        {loadingSaved ? (
          <p className="text-sm text-label-secondary">Kanäle werden geladen…</p>
        ) : savedChannels.length === 0 ? (
          <p className="text-sm text-label-secondary">
            Noch keine Kanäle importiert. Laden Sie unten eine Kanalliste hoch.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="eh-table w-full min-w-[32rem]">
              <thead>
                <tr>
                  <th>Kanal</th>
                  <th>Beschreibung (zfa.io)</th>
                  <th>CampDesk-Platz</th>
                </tr>
              </thead>
              <tbody>
                {savedChannels.map((row) => (
                  <tr key={row.channel_id}>
                    <td className="font-mono text-sm">K{row.channel_id}</td>
                    <td>{row.description}</td>
                    <td className="text-sm">
                      {row.pitch_code ? (
                        <span className="text-label">Platz {row.pitch_code}</span>
                      ) : (
                        <span className="text-label-tertiary">Nicht zugeordnet</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title="Kanalliste importieren" bodyClassName="space-y-4">
        <p className="text-sm text-label-secondary">
          Exportieren Sie die Zuordnungstabelle aus zfa.io (Format{" "}
          <code className="rounded bg-grouped px-1">Beschreibung;ID</code>) und ordnen Sie die
          Kanäle Ihren Stellplätzen zu.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm">
            <span className="mb-1 block text-label-secondary">CSV-Datei</span>
            <input
              type="file"
              accept=".csv,text/csv"
              disabled={!canWrite}
              onChange={onFileChange}
              className="block text-sm"
            />
          </label>
          <Button
            type="button"
            variant="secondary"
            loading={loading}
            disabled={!canWrite}
            onClick={() => void onPreview()}
          >
            Vorschau
          </Button>
        </div>
        <label className="block text-sm">
          <span className="mb-1 block text-label-secondary">Oder CSV-Inhalt einfügen</span>
          <textarea
            className="eh-input min-h-[8rem] font-mono text-xs"
            value={csvContent}
            disabled={!canWrite}
            onChange={(e) => {
              setCsvContent(e.target.value);
              setPreviewed(false);
            }}
            placeholder={"Beschreibung;ID\nPlatz 01;1\nPlatz 02;2"}
          />
        </label>
        {error ? <p className="text-sm text-status-red">{error}</p> : null}
        {success ? <p className="text-sm text-status-green">{success}</p> : null}
      </Card>

      {previewed ? (
        <Card
          title="Zuordnung"
          bodyClassName="space-y-4"
          actions={
            canWrite ? (
              <Button type="button" loading={applying} onClick={() => void onApply()}>
                {mappedCount} Zuordnung(en) speichern
              </Button>
            ) : undefined
          }
        >
          {warnings.length > 0 ? (
            <div className="rounded-lg border border-status-yellow/40 bg-status-yellow/10 p-3 text-sm text-label">
              <p className="font-medium">Hinweise zur automatischen Zuordnung</p>
              <ul className="mt-1 list-disc pl-5">
                {warnings.map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
            </div>
          ) : null}
          <div className="overflow-x-auto">
            <table className="eh-table w-full min-w-[40rem]">
              <thead>
                <tr>
                  <th>Kanal</th>
                  <th>Beschreibung (zfa.io)</th>
                  <th>Aktuell</th>
                  <th>CampDesk-Platz</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.channel_id}>
                    <td className="font-mono text-sm">K{row.channel_id}</td>
                    <td>{row.description}</td>
                    <td className="text-sm text-label-secondary">
                      {row.current_pitch_code ? `Platz ${row.current_pitch_code}` : "—"}
                    </td>
                    <td>
                      <PitchMappingAutocomplete
                        pitches={pitchesForRow(row.description)}
                        channelId={row.channel_id}
                        value={row.selected_pitch_id}
                        disabled={!canWrite}
                        onChange={(pitchId) =>
                          setRows((prev) =>
                            prev.map((entry) =>
                              entry.channel_id === row.channel_id
                                ? { ...entry, selected_pitch_id: pitchId }
                                : entry,
                            ),
                          )
                        }
                      />
                      {row.score != null && row.pitch_code ? (
                        <p className="mt-0.5 text-xs text-label-tertiary">
                          Vorschlag: {row.pitch_code} ({Math.round(row.score * 100)} %)
                        </p>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : null}
    </div>
  );
}

function PitchMappingAutocomplete({
  pitches,
  channelId,
  value,
  disabled,
  onChange,
}: {
  pitches: Pitch[];
  channelId: number;
  value: string;
  disabled?: boolean;
  onChange: (pitchId: string) => void;
}) {
  const selectedPitch = pitches.find((pitch) => pitch.id === value) ?? null;
  const option = selectedPitch ? pitchToOption(selectedPitch) : null;
  const loadOptions = useCallback(
    (query: string) => pitchSource(pitches, query),
    [pitches],
  );

  return (
    <div className="max-w-xs">
      <LockedAutocomplete
        placeholder="Platz suchen…"
        disabled={disabled}
        value={option}
        loadOptions={loadOptions}
        onChange={(next) => onChange(next?.value.id ?? "")}
        hint={
          selectedPitch &&
          selectedPitch.fernauslese_channel != null &&
          selectedPitch.fernauslese_channel !== channelId
            ? `Bereits Kanal K${selectedPitch.fernauslese_channel} zugeordnet`
            : undefined
        }
      />
    </div>
  );
}
